import React, { useState, useRef } from "react";
import axios from "axios";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, Button, FormControl, IconButton, CircularProgress } from "@mui/material";
import { bindActionCreators } from "redux";
import { useDispatch, useSelector } from "react-redux";
import { Loader } from "@egovernments/digit-ui-react-components";
import { getDocumentByFileStoreId } from "@egovernments/digit-ui-libraries/src/utils/fileType";
import { formMTDocActions } from "@egovernments/digit-ui-module-core/src/redux/actions";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { Controller, useForm } from "react-hook-form";

export default function KmlFileUploader({ name, label, hasLabel = true, isMandatory = false, validationMsg, required, tab, showError, maxFiles }) {
  const { documents } = useSelector((state) => state.formMT);
  const dispatch = useDispatch();
  const { addFormMTDoc, removeFormMTDoc } = bindActionCreators(formMTDocActions, dispatch);
  const { control } = useForm();

  const tenantId = Digit.ULBService.getCurrentTenantId();
  const [isLoading, setIsLoading] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [showToast, setShowToast] = useState(null);
  const [fileName, setFileName] = useState(""); // Track the file name state

  const fileInputRef = useRef(null); // Ref for hidden file input

  // Handle file upload
  const handleFileUpload = async (e, inputName) => {
    const file = e.target.files[0];
    setIsLoading(true);
    setShowToast(null);
    if (!file) {
      setShowToast({ error: true, label: "Please select a file." });
      setIsLoading(false);
      return;
    }

    // Validation logic for different tabs and file types
    const fileExtension = file.name.split(".").pop();
    const maxSizeInBytes = tab === 1 || tab === 2 ? 10 * 1024 * 1024 : 30 * 1024 * 1024; // 10MB or 30MB limit

    // Allowed formats for KML files
    const allowedFormats = [".kml", ".KML"];

    if (!allowedFormats.includes(`.${fileExtension}`)) {
      setShowToast({ error: true, label: `Please select a file with a valid format (.kml, .KML)` });
      setIsLoading(false);
      return;
    }
    if (documents?.filter((doc) => doc.documentType === name).length >= maxFiles) {
      setShowToast({ error: true, label: `You can upload a maximum of ${maxFiles} files.` });
      setIsLoading(false);
      return;
    }
    if (file.size > maxSizeInBytes) {
      setShowToast({ error: true, label: `File size exceeds the maximum limit of 10 MB.` });
      setIsLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("tenantId", tenantId);
    formData.append("module", "property-upload");
    formData.append("tag", "tag-property");

    try {
      const resp = await axios.post("/filestore/v1/files", formData, {});
      const filesId = resp.data.files[0];
      addFormMTDoc({
        documentType: inputName,
        fileStoreId: filesId.fileStoreId,
        isMandatory,
        additionalDetails: { fileName: file.name, title: label },
      });
      e.target.value = null;
      setFileName(file.name); // Update file name after upload
    } catch (error) {
      console.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <div className="inputContainer" style={{ alignItems: "flex-start" }}>
        {hasLabel && (
          <h3 className={`label`} style={{ width: "100%" }}>
            {label}
            {isMandatory && <span className="compulsoryField"> *</span>}
          </h3>
        )}

        <Box
          sx={{
            flexDirection: "column",
            position: "relative",
            width: "100%", // Make it responsive
            display: "flex",
          }}
        >
          {/* Custom File Input UI */}
          <FormControl fullWidth sx={{ display: "flex", alignItems: "center" }}>
            <Button
              variant="outlined"
              component="span"
              onClick={() => fileInputRef.current.click()} // Trigger file input click
              sx={{
                width: "100%",
                mb: 1,
                display: "flex",
                justifyContent: "space-between",
                height: "30px",
                backgroundColor: "rgb(21, 124, 179)",
                color: "white",
                "&:hover": {
                  backgroundColor: "#004e6c", // Darker shade of blue
                  color: "#fff", // Keep text white
                },
                position: "relative", // Positioning for validation message
              }}
              disabled={documents?.filter((doc) => doc.documentType === name).length >= maxFiles}
            >
              {isLoading ? (
                <Box
                  sx={{
                    justifyContent: "center",
                    position: "absolute",
                    alignItems: "center",
                    display: "flex",
                    inset: 0,
                  }}
                >
                  <CircularProgress sx={{ color: "#ffffff" }} size={28} />
                </Box>
              ) : (
                <React.Fragment>
                  <CloudUploadOutlinedIcon />
                  <span style={{ position: "absolute", fontSize: "0.5rem", marginLeft: "8em" }}>{validationMsg}</span>
                </React.Fragment>
              )}
            </Button>

            <Controller
              name="fileInput"
              control={control}
              rules={{
                required: required && !documents?.some((doc) => doc.documentType === name),
              }}
              render={({ field }) => (
                <input
                  ref={fileInputRef} // Create a ref for file input
                  type="file"
                  name={name}
                  onChange={(e) => handleFileUpload(e, name)} // Handle file change
                  style={{ display: "none" }} // Hide native file input
                />
              )}
            />
          </FormControl>

          {(documents?.length > 0 && !isLoading && (
            <Box
              sx={{
                maxWidth: "100%",
                display: "flex",

                flexDirection: "column",
              }}
            >
              {documents?.map((doc) => {
                if (doc.documentType !== name) return null;
                return (
                  <Box
                    sx={{
                      justifyContent: "space-between",
                      p: "0.25rem 0 0.25rem 0.75rem",
                      backgroundColor: "#eee",
                      borderRadius: "0.25rem",
                      wordBreak: "break-all",
                      alignItems: "center",
                      display: "flex",
                      lineHeight: 1,
                      flex: 1,
                      height: "18px",
                      fontSize: "0.6rem",
                    }}
                    key={doc.fileStoreId}
                  >
                    {doc.additionalDetails?.fileName || doc.documentType}
                    <Box sx={{ display: "flex" }}>
                      <IconButton
                        size="small"
                        color="info"
                        sx={{ fontSize: "1rem" }}
                        onClick={() => getDocumentByFileStoreId(doc.fileStoreId, setIsPreviewLoading)}
                      >
                        {isPreviewLoading ? <CircularProgress size={20} /> : <VisibilityIcon />}
                      </IconButton>
                      <IconButton size="small" color="error" sx={{ fontSize: "1rem" }} onClick={() => removeFormMTDoc(doc.documentType)}>
                        <DeleteForeverIcon />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )) ||
            null}
        </Box>
      </div>
      {required && showError && !documents?.some((doc) => doc.documentType === name) && (
        <span className="errorText">{`${label} document is required.`}</span>
      )}
      {!!showToast?.error && <span className="errorText">{showToast.label}</span>}
    </Box>
  );
}
